import React, { useEffect, useState } from 'react';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import ReactMarkdown from 'react-markdown';

// import Avatar from '@material-ui/core/Avatar';
// import CardMedia from '@material-ui/core/CardMedia';
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import ImageList from '@material-ui/core/ImageList';
import ImageListItem from '@material-ui/core/ImageListItem';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SvgIcon from '@material-ui/core/SvgIcon';
import { ReactComponent as StampIcon } from '../../assets/icons/Stamp.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/Check.svg';
import { ReactComponent as CrownIcon } from '../../assets/icons/Crown.svg';

import * as LoyaltyRewardService from '../../api/loyaltyRewardsService';
import * as RewardService from '../../api/rewardService';
import * as StampCampaignService from '../../api/stampCampaignService';
import * as StampCardService from '../../api/stampCardService';
import ViewMoreRewardsDialog from '../Dialogs/VIewMoreRewardsDialog';
import ViewCardDetailsDialog from '../Dialogs/ViewCardDetailsDialog';

import Paper from '../Paper';

const useStampStyles = makeStyles((theme) => ({
  root: ({ highlight }: { highlight: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: highlight ? 2 : 1,
    borderStyle: 'solid',
    borderColor: highlight
      ? theme.palette.primary.main
      : theme.palette.grey[300],
    width: 40,
    height: 40,
    borderRadius: 40 / 2,
  }),
  icon: {
    color: theme.palette.primary.main,
  },
  fillRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    width: 40,
    height: 40,
    borderRadius: 40 / 2,
    backgroundColor: theme.palette.primary.main,
  },
}));

export function Stamp({
  value,
  highlight,
  currentStamp,
}: {
  value: number;
  highlight: boolean;
  currentStamp: number;
}) {
  const classes = useStampStyles({ highlight });

  return (
    <>
      {value <= currentStamp ? (
        <div className={classes.fillRoot}>
          <SvgIcon
            component={CheckIcon}
            color="primary"
            className={classes.icon}
            viewBox={'0 0 30 30'}
          />
        </div>
      ) : (
        <div className={classes.root}>
          {highlight ? (
            <SvgIcon
              component={StampIcon}
              color="primary"
              className={classes.icon}
            />
          ) : (
            <Typography variant="subtitle2">{value}</Typography>
          )}
        </div>
      )}
    </>
  );
}

const useMilestoneStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
  },
  image: {
    width: 36,
    height: 36,
    marginRight: 10,
    borderRadius: 2,
    objectFit: 'cover',
  },
  content: {
    minWidth: 0, // Ensure the child text overflow ellipsis works on a flex child
  },
  stamps: {
    fontWeight: theme.typography.fontWeightBold,
  },
  title: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

/**
 * Milestone renders the milestone list item
 * @param milestone
 */
export function MilestoneListItem({
  milestone,
}: {
  milestone: LoyaltyRewardService.RedemptionMilestone;
}) {
  const classes = useMilestoneStyles();
  const [reward, setReward] = useState<RewardService.Reward | null>(null);

  useEffect(() => {
    const fetchMilestoneReward = async () => {
      const res = await RewardService.get(milestone.reward.id);
      setReward(res);
    };

    fetchMilestoneReward();
  }, [milestone]);

  if (!reward) {
    return null;
  }

  return (
    <div className={classes.root}>
      {reward && reward.images && reward.images[0].versions.xl && (
        <div>
          <img
            src={reward.images[0].versions.xl}
            alt={reward.title}
            className={classes.image}
          />
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <Typography variant="subtitle2" className={classes.stamps}>
          {milestone.threshold} Stamps get {reward.title}
        </Typography>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 16px',
    marginBottom: theme.spacing(),
    width: 350,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 1)',
    marginTop: -40,
    marginBottom: theme.spacing(),
  },
  stampCardRoot: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: `${theme.spacing()}px ${theme.spacing(2.5)}px 0`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(),
    },
  },
  stampCardRootOnlyActiveStampCard: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: `${theme.spacing()}px ${theme.spacing(2.5)}px 0`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(),
    },
  },
  tile: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(),
  },
  milestonesContainer: {
    marginTop: '5px',
  },
  minSpend: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(),
  },
  subtext: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: '12px',
  },
  subtextPrize: {
    fontSize: 12,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    lineHeight: '12px',
  },
  minSpendPrize: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(),
    textAlign: 'center',
  },
  noCardText: {
    fontSize: 16,
    fontWeight: 800,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    padding: theme.spacing(5),
  },
  descContainer: {
    marginTop: '5px',
  },
  descContainerPrize: {
    marginTop: '5px',
    marginBottom: theme.spacing(2),
  },
  descText: {
    fontWeight: theme.typography.fontWeightRegular,
    textAlign: 'center',
  },
  subtextContainerPrize: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  subtextContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  markdown: {
    fontFamily: 'ClarikaGeometric',
    fontSize: 16,
  },
  cardMedia: {
    height: 220,
    width: 350,
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
    backgroundSize: 'cover',
  },
  deleteCardContainer: {
    marginLeft: 'auto',
    marginBottom: theme.spacing(),
  },
  deleteCardContainerActive: {
    marginBottom: theme.spacing(3),
  },
  deleteIcon: {
    color: theme.palette.text.secondary,
    width: '20px',
    height: '20px',
  },
  deleteButton: {
    padding: '0 0 12px 12px',
  },
  merchantProfile: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: theme.spacing(4),
  },
  progress: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: '#ea710a',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  viewCardContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(),
  },
  crownIcon: {
    marginRight: theme.spacing(2),
  },
  actionMilestonesContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  viewCardButton: {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.grey[300],
  },
  actionMilestonesButton: {
    marginTop: 2,
    marginBottom: theme.spacing(),
  },
  detailContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: theme.spacing(2),
  },
}));

type Props = {
  stampCardId: string;
  merchantId: string;
  merchantName: string;
  merchantCoverImage?: string;
  merchantLogo?: string;
  maxStamps: number;
  milestones: LoyaltyRewardService.RedemptionMilestone[];
  quantity: number;
  handleAddStamp: () => void;
  stampCardStatus: string;
  handleDeleteCard: () => void;
  campaignType: string;
  onlyActiveStampCard?: boolean;
};

export default function StampCard({
  stampCardId,
  merchantId,
  merchantName,
  merchantCoverImage,
  merchantLogo,
  maxStamps,
  milestones,
  quantity,
  handleAddStamp,
  stampCardStatus,
  handleDeleteCard,
  campaignType,
  onlyActiveStampCard = false,
}: Props) {
  const classes = useStyles();
  const highlightedKeys = milestones.map((m) => m.threshold);
  const [stampCampaign, setStampCampaign] =
    useState<StampCampaignService.StampCampaign | null>(null);
  const [stampCard, setStampCard] = useState<StampCardService.StampCard | null>(
    null,
  );
  const filteredMilestones = milestones.filter((m) => m.threshold > quantity);
  const [showMoreRewards, setShowMoreRewards] = useState<boolean>(false);
  const [showCardDetails, setShowCardDetails] = useState<boolean>(false);

  useEffect(() => {
    const fetchStampCard = async () => {
      if (stampCardId) {
        const resStampCard = await StampCardService.get(stampCardId);
        setStampCard(resStampCard);
      }
    };

    if (stampCardId) {
      fetchStampCard();
    }
  }, [stampCardId]);

  useEffect(() => {
    const fetchStampCampaign = async () => {
      if (
        stampCard &&
        stampCard.loyalty_campaign &&
        stampCard.loyalty_campaign.id
      ) {
        const res = await StampCampaignService.get(
          stampCard.loyalty_campaign.id,
        );
        setStampCampaign(res);
      }
    };

    fetchStampCampaign();
  }, [stampCard]);

  if (!stampCard) {
    return null;
  }

  if (!stampCampaign) {
    return null;
  }

  return (
    <Grid item xs={12} className={classes.root}>
      <Paper className={classes.cardContainer}>
        {/* <CardMedia
          className={classes.cardMedia}
          image={merchantCoverImage}
          onClick={() => {
            if (stampCardStatus === 'active') {
              handleAddStamp();
            }
          }}
        />
        <div
          className={classes.merchantProfile}
          onClick={() => {
            if (stampCardStatus === 'active') {
              handleAddStamp();
            }
          }}
        >
          <Avatar
            alt={merchantName}
            src={merchantLogo}
            className={classes.avatar}
          />
          <Typography variant="h5" className={classes.title} gutterBottom>
            {stampCard.stampCampaignName}
          </Typography>
        </div> */}
        {onlyActiveStampCard && (
          <div
            className={classes.merchantProfile}
            onClick={() => {
              if (stampCardStatus === 'active') {
                handleAddStamp();
              }
            }}
          >
            <Typography variant="h5" className={classes.title} gutterBottom>
              {stampCard.stampCampaignName}
            </Typography>
          </div>
        )}
        <div
          className={
            !onlyActiveStampCard
              ? classes.stampCardRoot
              : classes.stampCardRootOnlyActiveStampCard
          }
          onClick={() => {
            if (stampCardStatus === 'active') {
              handleAddStamp();
            }
          }}
        >
          {(quantity <= maxStamps ||
            (stampCardStatus === 'completed' &&
              campaignType === 'PASSPORT')) && (
            <>
              <ImageList
                rowHeight={40}
                cols={maxStamps < 5 ? maxStamps : 5}
                gap={16}
                className={classes.tile}
              >
                {(() => {
                  if (maxStamps > 25) {
                    const start = Math.max(
                      quantity <= 25 ? 1 : Math.floor(quantity / 25) * 25 + 1,
                    );
                    const end = Math.min(start + 25, maxStamps + 1);

                    return range(start, end);
                  } else {
                    return range(1, maxStamps + 1);
                  }
                })().map((x) => {
                  return (
                    <ImageListItem
                      key={x}
                      cols={1}
                      rows={1}
                      className={classes.tile}
                    >
                      <Stamp
                        key={x}
                        value={x}
                        highlight={highlightedKeys.includes(x) && x > quantity}
                        currentStamp={quantity}
                      />
                    </ImageListItem>
                  );
                })}
              </ImageList>
            </>
          )}
        </div>
        {maxStamps > 25 && (
          <div className={classes.viewCardContainer}>
            <Button
              variant="outlined"
              className={classes.viewCardButton}
              onClick={() => setShowCardDetails(true)}
            >
              {'VIEW CARD'}
            </Button>
          </div>
        )}
        <div
          className={classes.progressContainer}
          onClick={() => {
            if (stampCardStatus === 'active') {
              handleAddStamp();
            }
          }}
        >
          <SvgIcon
            component={CrownIcon}
            color="primary"
            className={classes.crownIcon}
          />
          <Typography variant="h5" className={classes.progress} gutterBottom>
            {quantity < maxStamps
              ? `Your Progress: ${quantity} of ${maxStamps}`
              : 'Completed!'}
          </Typography>
        </div>
        {stampCampaign && stampCampaign.description.length > 0 && (
          <div
            className={
              milestones.length > 0
                ? classes.descContainer
                : classes.descContainerPrize
            }
            onClick={() => {
              if (stampCardStatus === 'active') {
                handleAddStamp();
              }
            }}
          >
            <ReactMarkdown className={classes.markdown}>
              {stampCampaign.description}
            </ReactMarkdown>
          </div>
        )}

        <div className={classes.detailContainer}>
          {filteredMilestones.length > 0 && (
            <div
              className={classes.milestonesContainer}
              onClick={() => {
                if (stampCardStatus === 'active') {
                  handleAddStamp();
                }
              }}
            >
              {orderBy(filteredMilestones, ['threshold'])
                .slice(0, 2)
                .map((m, idx) => (
                  <MilestoneListItem key={idx} milestone={m} />
                ))}
            </div>
          )}
          {milestones.length > 2 && (
            <div className={classes.actionMilestonesContainer}>
              <Button
                variant="text"
                className={classes.actionMilestonesButton}
                onClick={() => setShowMoreRewards(true)}
                color="primary"
              >
                {'VIEW MORE'}
              </Button>
            </div>
          )}
          {stampCampaign.details.length > 0 && stampCardStatus === 'active' && (
            <Typography
              className={
                milestones.length > 0 ? classes.minSpend : classes.minSpendPrize
              }
              gutterBottom
            >
              {stampCampaign.details}
            </Typography>
          )}
        </div>
        {stampCardStatus !== 'completed' ? (
          <div className={classes.deleteCardContainer}>
            <IconButton
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                handleDeleteCard();
              }}
              className={classes.deleteButton}
            >
              <DeleteIcon className={classes.deleteIcon} />
            </IconButton>
          </div>
        ) : (
          <div className={classes.deleteCardContainerActive} />
        )}
      </Paper>
      <ViewMoreRewardsDialog
        open={showMoreRewards}
        onClose={() => setShowMoreRewards(false)}
        milestoneList={milestones}
        stampCardName={stampCard.stampCampaignName}
      />
      <ViewCardDetailsDialog
        open={showCardDetails}
        onClose={() => setShowCardDetails(false)}
        milestoneList={milestones}
        stampCardName={stampCard.stampCampaignName}
        quantity={quantity}
        maxStamps={maxStamps}
        description={
          stampCampaign.description.length > 0
            ? stampCampaign.description
            : undefined
        }
      />
    </Grid>
  );
}
